/**
 * AddressAutocomplete - Google Places-powered address search
 *
 * Loads Google Maps Places API and provides typeahead address suggestions.
 * On selection, auto-populates street, city, state, postcode fields.
 */

import { useState, useRef, useEffect } from 'react';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';

export interface AddressResult {
  fullAddress: string;
  street: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (result: AddressResult) => void;
  disabled?: boolean;
  placeholder?: string;
}

// Global script load state
let googleMapsLoading = false;
const loadCallbacks: Array<() => void> = [];

function isGoogleMapsReady(): boolean {
  return typeof google !== 'undefined' && !!google.maps?.places;
}

function loadGoogleMapsScript() {
  // Already available (survives HMR and repeat mounts)
  if (isGoogleMapsReady()) {
    console.log('[GoogleMaps] API already available, no need to load');
    return Promise.resolve();
  }

  if (googleMapsLoading) {
    console.log('[GoogleMaps] Script already loading, queueing callback');
    return new Promise<void>((resolve) => {
      loadCallbacks.push(resolve);
    });
  }

  googleMapsLoading = true;
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // If no API key, skip loading - component will degrade to plain text input
  if (!apiKey) {
    console.warn('[GoogleMaps] No API key configured');
    googleMapsLoading = false;
    return Promise.reject(new Error('No Google Maps API key'));
  }

  console.log('[GoogleMaps] Checking for existing script tag');
  // Check for existing script tag to avoid duplicates
  const existing = document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
  if (existing) {
    // Script tag exists (possibly from @vis.gl/react-google-maps APIProvider)
    // but may have been loaded WITHOUT the Places library.
    console.log('[GoogleMaps] Found existing script tag, waiting for load...');
    return new Promise<void>((resolve, reject) => {
      const tryLoadPlaces = async () => {
        // Google Maps SDK loaded but Places library missing —
        // dynamically import it (supported in v3.56+ async mode)
        if (typeof google !== 'undefined' && google.maps && !google.maps.places) {
          console.log('[GoogleMaps] SDK loaded without Places library, importing dynamically...');
          try {
            await google.maps.importLibrary('places');
            console.log('[GoogleMaps] Places library imported successfully');
          } catch (err) {
            console.error('[GoogleMaps] Failed to import Places library:', err);
          }
        }
        if (isGoogleMapsReady()) {
          googleMapsLoading = false;
          resolve();
        } else {
          googleMapsLoading = false;
          reject(new Error('Google Maps Places library failed to load'));
        }
      };

      const onLoad = () => {
        console.log('[GoogleMaps] Existing script loaded');
        tryLoadPlaces();
      };
      const onError = () => {
        console.error('[GoogleMaps] Existing script failed to load');
        googleMapsLoading = false;
        reject(new Error('Failed to load Google Maps'));
      };

      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);

      // If script already loaded between our check and listener setup
      if (typeof google !== 'undefined' && google.maps) {
        console.log('[GoogleMaps] Script already loaded, checking Places...');
        existing.removeEventListener('load', onLoad);
        existing.removeEventListener('error', onError);
        tryLoadPlaces();
      }
    });
  }

  console.log('[GoogleMaps] Creating new script tag');
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    // loading=async is required by Google Maps JS API v3.56+ to prevent
    // the "This page can't load Google Maps correctly" error overlay
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('[GoogleMaps] Script loaded, waiting for Places library initialization...');

      // Poll for Places library to be ready (can take 50-200ms after script load)
      let attempts = 0;
      const maxAttempts = 40; // 2 seconds max
      const checkInterval = 50;

      const checkPlacesReady = () => {
        attempts++;
        if (isGoogleMapsReady()) {
          console.log(`[GoogleMaps] Places library ready after ${attempts * checkInterval}ms`);
          googleMapsLoading = false;
          resolve();
          loadCallbacks.forEach((cb) => cb());
          loadCallbacks.length = 0;
        } else if (attempts >= maxAttempts) {
          console.error('[GoogleMaps] Timeout waiting for Places library');
          googleMapsLoading = false;
          reject(new Error('Google Maps Places library failed to initialize'));
        } else {
          setTimeout(checkPlacesReady, checkInterval);
        }
      };

      checkPlacesReady();
    };
    script.onerror = (error) => {
      console.error('[GoogleMaps] Script failed to load:', error);
      googleMapsLoading = false;
      reject(new Error('Failed to load Google Maps'));
    };
    console.log('[GoogleMaps] Appending script to head, URL:', script.src);
    document.head.appendChild(script);
  });
}

/**
 * Dismiss the "This page can't load Google Maps correctly" overlay
 * that Google injects when the API key has billing/config issues.
 */
function dismissGoogleMapsErrorOverlay() {
  try {
    // Google injects a dismissable overlay with class 'dismissButton' or a specific style
    const overlays = document.querySelectorAll('.dismissButton, [style*="background-color: white"][style*="position: absolute"]');
    overlays.forEach((el) => {
      const parent = el.closest('[style*="z-index"]');
      if (parent && parent instanceof HTMLElement) {
        parent.style.display = 'none';
      }
    });
    // Also try to find and hide the standard Google Maps error banner
    const gmErrorBanners = document.querySelectorAll('.gm-err-container, .gm-style-cc');
    gmErrorBanners.forEach((el) => {
      if (el instanceof HTMLElement) el.style.display = 'none';
    });
  } catch {
    // Non-critical — ignore
  }
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  disabled,
  placeholder = 'Start typing an address...',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Use refs for callbacks so the Places listener always sees the latest
  // without tearing down and recreating the Autocomplete instance.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onAddressSelectRef = useRef(onAddressSelect);
  onAddressSelectRef.current = onAddressSelect;

  // Sync external value changes into the uncontrolled input
  // (e.g., when form is reset or value is set programmatically)
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    console.log('[AddressAutocomplete] Initializing, API key present:', !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
    setIsLoading(true);
    loadGoogleMapsScript()
      .then(() => {
        console.log('[AddressAutocomplete] Google Maps API loaded successfully');
        setApiAvailable(true);
        setApiError(null);
        setIsLoading(false);
        // Dismiss any error overlay Google may have injected
        setTimeout(dismissGoogleMapsErrorOverlay, 500);
      })
      .catch((err) => {
        console.error('[AddressAutocomplete] Failed to load Google Maps:', err);
        setApiAvailable(false);
        setApiError(err?.message === 'No Google Maps API key'
          ? 'Google Maps API key not configured — type address manually'
          : 'Google Maps failed to load — type address manually');
        setIsLoading(false);
        // Dismiss any error overlay Google may have injected
        setTimeout(dismissGoogleMapsErrorOverlay, 500);
        setTimeout(dismissGoogleMapsErrorOverlay, 2000);
      });
  }, []);

  // Create autocomplete once when API is ready — deps do NOT include callbacks
  useEffect(() => {
    if (!apiAvailable || !inputRef.current || autocompleteRef.current) return;

    // Triple-check Places library is actually loaded before creating instance
    if (!isGoogleMapsReady()) {
      console.error('[AddressAutocomplete] API marked available but Places library not ready');
      setApiError('Google Maps not fully loaded — please refresh page');
      setApiAvailable(false);
      return;
    }

    console.log('[AddressAutocomplete] Creating Autocomplete instance');
    try {
      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'au' },
        types: ['address'],
        fields: ['address_components', 'formatted_address'],
      });

      autocompleteRef.current.addListener('place_changed', () => {
        try {
          const place = autocompleteRef.current?.getPlace();
          if (!place?.address_components) return;

          let street = '';
          let city = '';
          let state = '';
          let postcode = '';
          let country = '';
          let streetNumber = '';
          let route = '';

          for (const component of place.address_components) {
            const types = component.types;
            if (types.includes('street_number')) streetNumber = component.long_name;
            if (types.includes('route')) route = component.long_name;
            if (types.includes('locality')) city = component.long_name;
            if (types.includes('administrative_area_level_1')) state = component.short_name;
            if (types.includes('postal_code')) postcode = component.long_name;
            if (types.includes('country')) country = component.long_name;
          }

          street = streetNumber ? `${streetNumber} ${route}` : route;
          const fullAddress = place.formatted_address ?? `${street}, ${city} ${state} ${postcode}`;

          onChangeRef.current(fullAddress);
          onAddressSelectRef.current({ fullAddress, street, city, state, postcode, country });
        } catch (error) {
          console.error('Error parsing address from Google Places:', error);
          setApiError('Error parsing address — please enter manually');
        }
      });
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setApiError('Failed to initialize address autocomplete — type manually');
      setApiAvailable(false);
    }

    return () => {
      if (autocompleteRef.current) {
        try {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch (error) {
          console.error('Error cleaning up autocomplete listeners:', error);
        }
        autocompleteRef.current = null;
      }
    };
  }, [apiAvailable]);

  return (
    <div>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted" />
        <input
          ref={inputRef}
          type="text"
          defaultValue={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={apiAvailable ? placeholder : 'Type address manually...'}
          disabled={disabled}
          className="w-full pl-10 pr-10 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted animate-spin" />
        )}
      </div>
      {apiError && (
        <div className="flex items-center gap-1.5 mt-1">
          <AlertCircle className="w-3 h-3 text-amber-400/60 shrink-0" />
          <p className="text-[10px] text-amber-400/60">{apiError}</p>
        </div>
      )}
    </div>
  );
}
